import { useEffect, useState } from "react";
import { DEMO_LIMITS, DEMO_TEMPLATE_FIELDS, type DemoPurgeResultDto, type DemoTemplate, type PlatformDemoDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { errText, fieldErrs, Title, when } from "./common";

// The public demo's settings (migration 055), Super Admin only: on or off (off after the migration), its bounded
// limits, the demo business's information that visitors cannot change, its usage, and deleting ended demos now.

const LIMITS: [keyof typeof DEMO_LIMITS, string][] = [["minutes", "pfDemoMinutes"], ["maxActive", "pfDemoMaxActive"], ["perIpHour", "pfDemoPerIp"], ["dailyMax", "pfDemoDaily"], ["maxWrites", "pfDemoWrites"]];
const TEMPLATE: Record<keyof DemoTemplate, string> = { nameEn: "pfDemoNameEn", nameAr: "pfDemoNameAr", city: "pfDemoCity", street: "pfDemoStreet", phone: "pfDemoPhone", email: "pfDemoEmail", taxNumber: "pfDemoTax", commercialRecord: "pfDemoCr" };

export function DemoSettings() {
  const { t, lang } = useLang();
  const toast = useToast();
  const [s, setS] = useState<PlatformDemoDto | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const load = () => api<PlatformDemoDto>("GET", "/platform/demo").then(setS, (e) => setFailed(errText(t, e)));
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (failed) return <><Title text={t("pfDemoTitle")} /><div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div></>;
  if (!s) return <><Title text={t("pfDemoTitle")} /><div className="state-box"><p>{t("pfLoading")}</p></div></>;
  const save = async () => {
    try {
      setS(await api("PUT", "/platform/demo", { enabled: s.enabled, minutes: s.minutes, maxActive: s.maxActive, perIpHour: s.perIpHour, dailyMax: s.dailyMax, maxWrites: s.maxWrites, template: s.template }));
      setErrs({}); toast(t("pfDemoSaved"));
    } catch (e) { setErrs(fieldErrs(t, e)); toast(errText(t, e)); }
  };
  const purge = async () => {
    try { const r = await api<DemoPurgeResultDto>("POST", "/platform/demo/purge"); toast(t("pfDemoPurged2", { n: r.purged })); await load(); }
    catch (e) { toast(errText(t, e)); }
  };
  return (
    <>
      <Title text={t("pfDemoTitle")} />
      <section className="card" data-testid="pf-demo"><div className="pf-card-b">
        <p className="pf-small">{t("pfDemoIntro")}</p>
        <label className="check"><input type="checkbox" data-testid="pf-demo-enabled" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} /> <b>{t("pfDemoEnabled")}</b></label>
        {!s.enabled && <span className="pf-small" data-testid="pf-demo-off">{t("pfDemoOff")}</span>}
        <div className="ob-form">{LIMITS.map(([k, l]) => (
          <div key={k} className={`field ${errs[k] ? "invalid" : ""}`}><label htmlFor={`pd-${k}`}>{t(l)}</label>
            <input id={`pd-${k}`} className="control num" type="number" min={DEMO_LIMITS[k][0]} max={DEMO_LIMITS[k][1]} value={String(s[k])} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} /><div className="ferr">{errs[k]}</div></div>
        ))}</div>
        <b>{t("pfDemoTemplate")}</b><span className="pf-small">{t("pfDemoTemplateHint")}</span>
        <div className="ob-form">{DEMO_TEMPLATE_FIELDS.map((k) => (
          <div key={k} className={`field ${errs[`template.${k}`] ? "invalid" : ""}`}><label htmlFor={`pdt-${k}`}>{t(TEMPLATE[k])}</label>
            <input id={`pdt-${k}`} className="control" dir={k === "nameAr" ? "rtl" : undefined} value={s.template[k]} onChange={(e) => setS({ ...s, template: { ...s.template, [k]: e.target.value } })} /><div className="ferr">{errs[`template.${k}`]}</div></div>
        ))}</div>
        <div className="pf-row"><button className="btn btn-primary" data-testid="pf-demo-save" onClick={() => void save()}>{t("save")}</button>
          <span className="pf-small">{when(s.updatedAt, lang)}</span></div>
      </div></section>
      <section className="card" data-testid="pf-demo-usage"><div className="pf-card-b">
        <b>{t("pfDemoUsage")}</b>
        <div className="pf-row">
          {([["active", "pfDemoActive"], ["last24h", "pfDemo24h"], ["awaitingPurge", "pfDemoAwaiting"], ["purged", "pfDemoPurged"]] as const).map(([k, l]) => (
            <span key={k} className="pill" data-testid={`pf-demo-${k}`}>{t(l)}: <b className="num">{s.usage[k]}</b></span>
          ))}
        </div>
        <div className="pf-row"><button className="btn" data-testid="pf-demo-purge" onClick={() => void purge()}><Icon name="trash" />{t("pfDemoPurge")}</button></div>
      </div></section>
    </>
  );
}
